/* eslint-disable camelcase */
/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Modal from 'react-modal';
import DatePicker from 'react-datepicker';
import Select from 'react-select';

import * as api from '../../epics/api';
import DocumentModal from './DocumentModal';
import { ErrorMessage } from '../common';
import { getErrorMessages, isImage } from '../../utils';
import { UploadFile } from '../common/UploadFile';
import { TDocuments, TDocument, TFreelancerData } from '../../types';
import { TDocumentRejectionReason } from './FreelancerDetails';
import { freelancerRolesDetails } from '../../consts';

let axiosAbortController: any;

type THandleAddDocument = {
    data: any;
    setUploadFileDetails: (a: any) => void;
    setUploadPercentage: (a: number) => void;
    ajaxOptions: any;
    setDocumentType: (a: any) => void;
    setDocumentSubtype: (a: string) => void;
    setExpiryDate: (a: any) => void;
    setSortCode: (a: string) => void;
    setAccountNumber: (a: string) => void;
    setDrivingLicenceNumber: (a: string) => void;
    handleClose: () => void;
};

export type TPossibleDocument = {
    type: string;
    label: string;
    short_label: string;
    additional_description?: string;
    rich_description: string;
    expires: boolean;
    required: boolean;
    zego?: boolean;
    allowed_files: number;
    agent_required_files: number;
    freelancer_required_files: number;
    subtypes: {
        [key: string]: string;
    };
    auto_review: boolean;
    display_tab: boolean;
    multiple_uploads: boolean;
};

export type TModalAddDocumentProps = {
    abortController: {
        signal: () => void;
    };
    errorMessage: () => void;
    handleClose: () => void;
    freelancerId: any;
    possibleDocuments: TPossibleDocument[];
    handleAddDocument: (a: THandleAddDocument) => void;
    replaceableDocuments: TDocument[];
    isError: boolean;
    isOpen: boolean;
};

const BANK_REFERENCES = 'BR';
const DBS_CHECK = 'DB';
const DRIVING_LICENCE = 'DL';


export function ModalAddDocument({
    abortController,
    errorMessage,
    handleAddDocument,
    handleClose,
    freelancerId,
    possibleDocuments,
    replaceableDocuments,
    isError,
    isOpen,
}: TModalAddDocumentProps) {
    const [uploadPercentage, setUploadPercentage] = useState(0);
    const [uploadFileDetails, setUploadFileDetails] = useState(null);
    const [uploadFile2Details, setUploadFile2Details] = useState(null);
    const [documentType, setDocumentType] = useState(null);
    const [documentSubtype, setDocumentSubtype] = useState(null);
    const [expiryDate, setExpiryDate] = useState(null);
    const [sortCode, setSortCode] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [certificateNumber, setCertificateNumber] = useState('');
    const [consentCode, setConsentCode] = useState('');
    const [drivingLicenceNumber, setDrivingLicenceNumber] = useState('');
    const [name, setName] = useState('');
    const [oldDocumentId, setOldDocumentId] = useState(null);

    useEffect(() => {
        const value = documentType?.value === DBS_CHECK
            ? moment().add('6', 'months').toDate()
            : null;
        setExpiryDate(value);
    }, [documentType]);

    const isFileBeingUploaded = uploadPercentage && !isError;

    const selectedIndex = possibleDocuments.find(
        el => el.type === documentType?.value
    );

    const shouldDisplayExpiryField = selectedIndex?.expires;

    const isMultipleUploadsDocSelected = selectedIndex?.multiple_uploads;

    const subtypeOptions =
        selectedIndex?.subtypes &&
        Object.entries(selectedIndex?.subtypes).map(subtype => ({
            label: subtype[1],
            value: subtype[0],
        }));

    const typeOptions = possibleDocuments.map(type => ({
        label: type.label,
        value: type.type,
    }));

    const oldDocumentOptions = replaceableDocuments.map(doc => ({
        label: `${doc.name} (uploaded: ${moment(doc.created).format('D MMM YYYY, HH:mm')})`,
        value: doc.id
    }));

    const subTypeOptionsIsNotEmpty = !!(Array.isArray(subtypeOptions) && subtypeOptions.length);

    const subTypeIsDisplayed =
        subTypeOptionsIsNotEmpty && (documentSubtype === null || documentSubtype?.value === '');

    const saveButtonDisabled =
        (!!uploadPercentage && !isError) || subTypeIsDisplayed;

    const handleSave = () => {
        const documentWithFileFormData = new FormData();

        documentWithFileFormData.append('type', documentType?.value);
        documentWithFileFormData.append('freelancer', freelancerId);

        if (documentSubtype) {
            documentWithFileFormData.append('subtype', documentSubtype?.value);
        }

        if (shouldDisplayExpiryField && expiryDate instanceof Date) {
            documentWithFileFormData.append(
                'expiry',
                expiryDate.toISOString().split('T')[0],
            );
        }

        if (documentType.value === BANK_REFERENCES) {
            if (sortCode) {
                documentWithFileFormData.append('sort_code', sortCode);
            }
            if (accountNumber) {
                documentWithFileFormData.append('account_number', accountNumber);
            }
        }

        if (documentType.value === DBS_CHECK && certificateNumber) {
            if (certificateNumber) {
                documentWithFileFormData.append('certificate_number', certificateNumber);
            }
            if (consentCode) {
                documentWithFileFormData.append('consent_code', consentCode);
            }
        }

        if (documentType.value === DRIVING_LICENCE) {
            if (drivingLicenceNumber) {
                documentWithFileFormData.append('driving_licence_number', drivingLicenceNumber);
            }
        }

        if (isMultipleUploadsDocSelected) {
            if (name) {
                documentWithFileFormData.append('name', name);
            }
            if (oldDocumentId) {
                documentWithFileFormData.append('old_document', oldDocumentId);
            }
        }

        if (uploadFileDetails) {
            documentWithFileFormData.append('file', uploadFileDetails);
        }

        if (uploadFile2Details) {
            documentWithFileFormData.append('file2', uploadFile2Details);
        }

        const ajaxOptions = {
            onUploadProgress: (progressEvent: {
                loaded: number;
                total: number;
            }) => {
                const { loaded, total } = progressEvent;
                const percent = Math.floor((loaded * 100) / total);

                if (percent < 100) {
                    setUploadPercentage(percent);
                }
            },
            signal: abortController.signal,
        };

        handleAddDocument({
            data: documentWithFileFormData,
            setUploadFileDetails,
            setUploadPercentage,
            ajaxOptions,
            setDocumentType,
            setDocumentSubtype,
            setExpiryDate,
            setSortCode,
            setAccountNumber,
            setDrivingLicenceNumber,
            handleClose,
        });
    };

    return (
        <Modal
            className="add-file-modal"
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Add Document"
            ariaHideApp={false}
        >
            <h4>Add document</h4>
            <div className="form">
                <label htmlFor="document_type">Type:</label>
                <Select
                    name="document-type"
                    value={documentType}
                    clearable={false}
                    onChange={event => {
                        setDocumentType({
                            label: event.label,
                            value: event.value,
                        });
                        setDocumentSubtype({
                            label: '',
                            value: '',
                        });
                    }}
                    options={typeOptions}
                    isDisabled={isFileBeingUploaded}
                />
                {subTypeOptionsIsNotEmpty && (
                    <>
                        <label htmlFor="document_subtype">Subtype:</label>
                        <Select
                            name="document-subtype"
                            value={documentSubtype}
                            clearable={false}
                            onChange={event => {
                                setDocumentSubtype({
                                    label: event.label,
                                    value: event.value,
                                });
                            }}
                            options={subtypeOptions}
                            isDisabled={isFileBeingUploaded}
                        />
                        {documentType?.value === BANK_REFERENCES && (
                            <>
                                <label htmlFor="sort_code">Sort code:</label>
                                <input
                                    type="text"
                                    maxLength={6}
                                    onChange={event =>
                                        setSortCode(event.target.value)
                                    }
                                />
                                <label htmlFor="account_number">
                                    Account number:
                                </label>
                                <input
                                    type="text"
                                    maxLength={8}
                                    onChange={event =>
                                        setAccountNumber(event.target.value)
                                    }
                                />
                            </>
                        )}
                        {documentType?.value === DBS_CHECK && (
                            <>
                                <label htmlFor="certificate_number">Certificate number:</label>
                                <input
                                    type="text"
                                    maxLength={32}
                                    onChange={event =>
                                        setCertificateNumber(event.target.value)
                                    }
                                />
                                <label htmlFor="consent_code">Consent code:</label>
                                <input
                                    type="text"
                                    maxLength={32}
                                    onChange={event =>
                                        setConsentCode(event.target.value)
                                    }
                                />
                            </>
                        )}
                    </>
                )}
                {documentType?.value === DRIVING_LICENCE && (
                    <>
                        <label htmlFor="driving_licence_number">Driving licence number:</label>
                        <input
                            type="text"
                            maxLength={32}
                            onChange={event =>
                                setDrivingLicenceNumber(event.target.value)
                            }
                        />
                    </>
                )}
                {shouldDisplayExpiryField && (
                    <div>
                        <label htmlFor="expiry_date">Expiry date:</label>
                        <DatePicker
                            selected={expiryDate}
                            onChange={date => setExpiryDate(date)}
                            peekNextMonth={true}
                            showMonthDropdown={true}
                            showYearDropdown={true}
                            dropdownMode="select"
                            dateFormat="dd/MM/yyyy"
                        />
                    </div>
                )}
                {isMultipleUploadsDocSelected && (
                    <>
                        <label htmlFor="name">Name:</label>
                        <input
                            type="text"
                            maxLength={64}
                            onChange={event =>
                                setName(event.target.value)
                            }
                        />
                        <label htmlFor="document-old-document">Old document:</label>
                        <Select
                            name="document-old-document"
                            placeholder="Select a document to be replaced..."
                            value={oldDocumentId}
                            onChange={event => setOldDocumentId(event.value)}
                            options={oldDocumentOptions}
                            isDisabled={isFileBeingUploaded}
                        />
                    </>
                )}
                <UploadFile
                    handleUploadFile={(event: { target: { files: any[] } }) =>
                        setUploadFileDetails(event.target.files[0])
                    }
                    uploadPercentage={uploadPercentage}
                    fileName={uploadFileDetails?.name}
                />
                {selectedIndex?.allowed_files === 2 && (
                    <UploadFile
                        handleUploadFile={(event: {
                            target: { files: any[] };
                        }) => setUploadFile2Details(event.target.files[0])}
                        uploadPercentage={uploadPercentage}
                        fileName={uploadFile2Details?.name}
                    />
                )}
                {isError && <ErrorMessage message={errorMessage} />}
                <div className="buttons-container">
                    <button
                        className="btn btn--round-small"
                        onClick={handleSave}
                        disabled={saveButtonDisabled}
                        type="button"
                    >
                        SAVE
                    </button>
                    <button
                        className="btn cancel-btn"
                        onClick={handleClose}
                        type="button"
                    >
                        CANCEL
                    </button>
                </div>
            </div>
        </Modal>
    );
}

type TFreelancerDetailsDocumentsProps = {
    freelancerData: TFreelancerData;
    fetchListOf: (a: string, b: string) => Promise<any>;
    editionEnabled: boolean;
    documents: TDocuments;
    documentRejectionReasonOptions: TDocumentRejectionReason[];
};

export default function FreelancerDetailsDocuments({
    freelancerData,
    fetchListOf,
    editionEnabled,
    documents,
    documentRejectionReasonOptions,
}: TFreelancerDetailsDocumentsProps) {
    const [isModalAddDocumentOpen, setIsModalAddDocumentOpen] = useState(false);
    const [deletingID, setDeletingID] = useState(null);
    const [editingID, setEditingID] = useState(null);
    const [fields, setFields] = useState({
        is_valid: false,
        expiry: new Date(),
        type: null,
        rejection_reason: null,
    });
    const [error, setError] = useState(null);
    const [popupDocument, setPopupDocument] = useState(null);
    const [popupDocument2, setPopupDocument2] = useState(null);
    const [isSelectRejectionReasonValid, setIsSelectRejectionReasonValid] = useState(true);
    const [isSelectExpiryDateValid, setIsSelectExpiryDateValid] = useState(true);

    useEffect(() => {
        axiosAbortController = new AbortController();
    }, []);

    const replaceableDocuments = documents.content
        .filter(document => {
            const possibleDocument = freelancerData.possible_documents
                .find(doc => doc.type === document.type);
            return possibleDocument?.multiple_uploads && !document.new_document;
        });

    const handleOpenAddDocumentModal = () => {
        axiosAbortController = new AbortController();
        setIsModalAddDocumentOpen(true);
        setError(null);
    };

    const handleCloseAddDocumentModal = () => {
        axiosAbortController.abort();
        setIsModalAddDocumentOpen(false);
        setError(null);
    };

    const openPopup = (document: TDocument) => {
        setPopupDocument(document);
    };

    const closePopup = () => {
        setPopupDocument(null);
    };

    const openPopup2 = (document: TDocument) => {
        setPopupDocument2(document);
    };

    const closePopup2 = () => {
        setPopupDocument2(null);
    };

    const handleAddDocument = ({
        data,
        setUploadFileDetails,
        setUploadPercentage,
        ajaxOptions,
        setDocumentType,
        setDocumentSubtype,
        setExpiryDate,
        setSortCode,
        setAccountNumber,
        handleClose,
    }: THandleAddDocument) => {
        setError(null);

        api.executeHttpRequest('documents/', 'post', data, true, ajaxOptions)
            .then(() => {
                setDocumentType('');
                setDocumentSubtype('');
                setExpiryDate('');
                setSortCode('');
                setAccountNumber('');
                setUploadPercentage(100);
                setUploadFileDetails(null);
                handleClose();

                fetchListOf(
                    'documents',
                    `documents/?freelancer=${freelancerData.id}`,
                ).then(() => {
                    handleClose();
                    setError(null);
                });
            })
            .catch(err => {
                setUploadPercentage(0);
                setError(getErrorMessages(err));
            });
    };

    const handleSelectRejectionReason = (event: any) => {
        setFields(documentFields => ({
            ...documentFields,
            rejection_reason: event.value,
        }));
        if (event.value) {
            setIsSelectRejectionReasonValid(true);
        }
    };

    const handleEditButtonClick = (document: TDocument) => {
        setEditingID(document.id);
        setFields({
            is_valid: document.is_valid,
            expiry: document.expiry ? new Date(document.expiry) : null,
            type: document.type,
            rejection_reason: document.rejection_reason,
        });
    };

    const handleCancelButtonClick = () => {
        setEditingID(null);
        setDeletingID(null);
        setFields({
            is_valid: null,
            expiry: null,
            type: null,
            rejection_reason: null,
        });
        setIsSelectRejectionReasonValid(true);
        setIsSelectExpiryDateValid(true);
    };

    const validateExpiryDate = (document: TDocument) => {
        if (document.expires && !fields.expiry) {
            setIsSelectExpiryDateValid(false);
            return  false;
        }
        return true;
    };

    const validateRejectionReason = () => {
        if (fields.is_valid === false && fields.rejection_reason === null) {
            setIsSelectRejectionReasonValid(false);
            return false;
        }
        return true;
    };

    const fieldsAreValid = (document: TDocument) =>
        validateExpiryDate(document) && validateRejectionReason();

    const handleSaveButtonClick = (document: TDocument) => {
        if (fieldsAreValid(document)) {
            setEditingID(document.id);
            api.executeHttpRequest(`documents/${document.id}/`, 'patch', {
                ...fields,
                expiry: fields.expiry?.toISOString().split('T')[0],
            })
                .then(() => {
                    fetchListOf(
                        'documents',
                        `documents/?freelancer=${freelancerData.id}`,
                    ).then(() => {
                        const serviceFilterName = freelancerRolesDetails[freelancerData.service_key];
                        fetchListOf(
                            'roles',
                            `freelancers/${freelancerData.id}/${serviceFilterName}/`
                        ).then(() => {
                            handleCancelButtonClick();
                            setError(null);
                        });
                    });
                })
                .catch(err => {
                    setError(getErrorMessages(err));
                });
            setIsSelectRejectionReasonValid(true);
            setIsSelectExpiryDateValid(true);
        }
    };

    const handleDeleteButtonClick = (document: TDocument) => {
        setDeletingID(document.id);

        api.executeHttpRequest(`documents/${document.id}/`, 'delete')
            .then(() => {
                fetchListOf(
                    'documents',
                    `documents/?freelancer=${freelancerData.id}`,
                ).then(() => {
                    handleCancelButtonClick();
                    setError(null);
                });
            })
            .catch(err => {
                handleCancelButtonClick();
                setError(getErrorMessages(err));
            });
    };

    const clearRejectionReasonField = () => {
        setFields(documentFields => ({
            ...documentFields,
            rejection_reason: null,
        }));
    };

    const handleCheckboxFieldChange = (event: any, fieldName: string, rejectionReason: string) => {
        // eslint-disable-next-line no-shadow
        setFields(fields => ({
            ...fields,
            [fieldName]: event.target.checked,
        }));
        if (event.target.checked) {
            setIsSelectRejectionReasonValid(true);
            clearRejectionReasonField();
        } else {
            setFields(documentFields => ({
                ...documentFields,
                rejection_reason: rejectionReason,
            }));
        }
    };

    const handleTextFieldChange = (event: any, fieldName: string) => {
        // eslint-disable-next-line no-shadow
        setFields(fields => ({
            ...fields,
            [fieldName]: event.target.value,
        }));
    };

    const handleDayChange = (date: Date) => {
        setFields(prevFields => ({
            ...prevFields,
            expiry: date,
        }));
        if (date) {
            setIsSelectExpiryDateValid(true);
        }
    };

    const renderActionButtons = (document: TDocument) => {
        if (editionEnabled) {
            if (editingID === document.id) {
                return (
                    <td className="actions center">
                        <button
                            onClick={() => handleSaveButtonClick(document)}
                            type="button"
                        >
                            <i className="icon icon icon-check-12" />
                        </button>
                        <button onClick={handleCancelButtonClick} type="button">
                            <i className="icon icon-close2" />
                        </button>
                    </td>
                );
            }

            return (
                <td className="actions center">
                    <button
                        onClick={() => handleEditButtonClick(document)}
                        type="button"
                    >
                        <i className="icon icon-pencil-3" />
                    </button>
                    <button
                        disabled={deletingID === document.id}
                        onClick={() => handleDeleteButtonClick(document)}
                        type="button"
                    >
                        <i className="icon icon-bin-1" />
                    </button>
                </td>
            );
        }

        return null;
    };

    const canBeValidated = (document: TDocument) =>
        // DBS check should not be validated from Freelancer Details view (interview and 'Documents review' only)
        !(document.type === DBS_CHECK && !document.is_valid);

    const getDocumentInfo = (document: TDocument) =>
        freelancerData.possible_documents.find((doc: TPossibleDocument) => doc.type === document.type);

    const getDocumentName = (document: TDocument) =>
        document.name ? `${document.name} (${document.label})` : document.label;

    const renderLink = (document: TDocument, file: any, num: number) => {
        const funcs: { [key: number]: (doc: TDocument) => void } = { 1: openPopup, 2: openPopup2 };
        const label = `${getDocumentName(document)}${document.file2 ? ` (${num})` : ''}`;

        if (isImage(file)) {
            return <a onClick={() => funcs[num](document)}>{label}</a>;
        }
        return <a href={file} target="_blank" rel="noreferrer" download={true}>{label}</a>;
    };

    const renderRows = () =>
        documents.content.map((document: any) => {
            const expiration = document.expiry
                ? moment(document.expiry).format('D MMM YYYY')
                : 'None';
            const documentInfo = getDocumentInfo(document);

            return (
                <tr key={document.id}>
                    {(editingID === document.id && documentInfo?.multiple_uploads) ? (
                        <td>
                            <input
                                name="name"
                                type="text"
                                maxLength={64}
                                defaultValue={document.name}
                                onChange={event => {
                                    handleTextFieldChange(event, 'name');
                                }}
                            />
                        </td>
                    ) : (
                        <td>
                            {document.file
                                ? renderLink(document, document.file, 1)
                                : <span>{getDocumentName(document)}</span>
                            }
                            {document.file2 &&
                                <>
                                    <br/>
                                    {renderLink(document, document.file2, 2)}
                                </>
                            }
                        </td>
                    )}
                    <td>
                        {document.subtype_description}
                    </td>
                    <td>
                        {moment(document.created).format('D MMM YYYY, HH:mm')}
                    </td>
                    {editingID === document.id && document.expires ? (
                        <td>
                            <DatePicker
                                className={isSelectExpiryDateValid ? '' : 'input-required'}
                                selected={fields.expiry}
                                onChange={date => handleDayChange(date)}
                                showMonthDropdown={true}
                                showYearDropdown={true}
                                dropdownMode="select"
                                dateFormat="dd/MM/yyyy"
                                minDate={new Date(1970, 0, 1)}
                                maxDate={new Date(2099, 11, 31)}
                                placeholderText={isSelectExpiryDateValid ? 'Select...' : 'Required!'}
                            />
                        </td>
                    ) : (
                        <td>{expiration}</td>
                    )}
                    <td>
                        {editingID === document.id && canBeValidated(document) ? (
                            <input
                                name="valid"
                                className="checkbox-purple"
                                type="checkbox"
                                checked={fields.is_valid}
                                onChange={event => {
                                    handleCheckboxFieldChange(
                                        event,
                                        'is_valid',
                                        document.rejection_reason
                                    );
                                }}
                            />
                        ) : (
                            <i
                                className={
                                    document.is_valid
                                        ? 'icon icon-check-12'
                                        : document.is_valid === false
                                            ? 'icon icon-close2'
                                            : ''
                                }
                            />
                        )}
                    </td>
                    <td>
                        {editingID === document.id && !fields.is_valid && canBeValidated(document) ? (
                            <Select
                                name="document-rejection-reason"
                                style={isSelectRejectionReasonValid ? (
                                    {}
                                ) : (
                                    {
                                        border: '1px solid red',
                                        background: '#ffcccb',
                                    }
                                )}
                                value={fields.rejection_reason}
                                clearable={false}
                                onChange={event => {
                                    handleSelectRejectionReason(event);
                                }}
                                options={documentRejectionReasonOptions}
                                placeholder={isSelectRejectionReasonValid ? (
                                    'Select...'
                                ) : (
                                    'Required!'
                                )}
                            />
                        ) : (
                            fields.is_valid ? '' : document.rejection_description
                        )}
                    </td>
                    {renderActionButtons(document)}
                </tr>
            );
        });

    return (
        <div className="freelancer-details-section">
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__title">
                    Documents
                </div>
                <div className="freelancer-details-section__btns display-block">
                    <button
                        className="freelancer-details-section__btn"
                        onClick={handleOpenAddDocumentModal}
                        type="button"
                    >
                        <i className="icon with-text icon-add-12" />
                        Add
                    </button>
                    {!!documents.loading && (
                        <i className="icon icon-loading-1 font-size-30" />
                    )}
                </div>
            </div>
            {error && !isModalAddDocumentOpen && (
                <div className="freelancer-details-section__errors">
                    <p>{error}</p>
                </div>
            )}
            <div className="freelancer-details-section__content">
                {documents.content.length === 0 ? (
                    <div>Worker doesn&apos;t have any documents yet.</div>
                ) : (
                    <div>
                        <table className="table-default margin-bottom-20">
                            <tbody>
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Has all valid documents
                                    </td>
                                    <td>
                                        {freelancerData.has_valid_documents
                                            ? 'Yes'
                                            : 'No'}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <table className="table-default">
                            <tbody>
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Type
                                    </td>
                                    <td className="table-default__cell--text-blue">
                                        Subtype
                                    </td>
                                    <td className="table-default__cell--text-blue">
                                        Uploaded
                                    </td>
                                    <td className="table-default__cell--text-blue">
                                        Expiration
                                    </td>
                                    <td className="table-default__cell--text-blue">
                                        Valid
                                    </td>
                                    <td className="table-default__cell--text-blue">
                                        Rejection Reason
                                    </td>
                                    {editionEnabled && (
                                        <td className="table-default__cell--text-blue center" />
                                    )}
                                </tr>
                                {renderRows()}
                            </tbody>
                        </table>
                        {popupDocument && (
                            <DocumentModal
                                isOpen={true}
                                src={popupDocument.file}
                                onClose={closePopup}
                                title={popupDocument.label}
                            />
                        )}
                        {popupDocument2 && (
                            <DocumentModal
                                isOpen={true}
                                src={popupDocument2.file2}
                                onClose={closePopup2}
                                title={popupDocument2.label}
                            />
                        )}
                    </div>
                )}
                {isModalAddDocumentOpen && (
                    <ModalAddDocument
                        abortController={axiosAbortController}
                        errorMessage={error}
                        handleAddDocument={handleAddDocument}
                        handleClose={handleCloseAddDocumentModal}
                        freelancerId={freelancerData.id}
                        possibleDocuments={freelancerData.possible_documents}
                        replaceableDocuments={replaceableDocuments}
                        isError={!!error}
                        isOpen={true}
                    />
                )}
            </div>
        </div>
    );
}
